/* The Score tab's explanation: what is helping a score, what is hurting it, and how far it can be
 * relied on. Pure — no React, no i18n — so every decision the screen makes is tested here.
 *
 * **Every figure is the API's.** `GET /scores/{id}/factors` returns the stored decomposition, whose
 * rules' points sum to the score and whose losses sum to 100 − score. The only arithmetic in this file
 * is for display: a headline's "together" sum, and "if it passed" as `value + points_lost`. Neither
 * re-derives a figure the platform stored, because two derivations of one number drift by rounding
 * and a screen whose parts do not add up to its total is the decomposition contradicting itself.
 *
 * **Absence is never zero.** A score that measured nothing has `value: null`, and a dimension with no
 * evidence has no points rather than zero points; both stay `null` here and the screen says why in
 * words. `percent` and `points` refuse the two roundings that would lie in this direction — a rule that
 * lost points reading as "100%", and a positive figure reading as "0".
 */

import type {
  ContextKind,
  Dimension,
  DimensionFactor,
  DimensionState,
  FactorDirection,
  RuleCheck,
  RuleFactor,
  ScoreFactors,
  Severity,
} from '../api/scoring';

import { DIMENSION_ORDER } from './dimensions';

// ---- numbers ----------------------------------------------------------------------------------

/** Points to two places. A positive figure too small to show says so rather than reading "0.00",
 * which would claim a rule that cost something cost nothing. */
export function points(value: number): string {
  return value > 0 && value < 0.005 ? '<0.01' : value.toFixed(2);
}

/** Points to one place, for the figures a headline and a segment label carry. */
export function pointsShort(value: number): string {
  return value > 0 && value < 0.05 ? '<0.1' : value.toFixed(1);
}

/** A ratio as a percentage. Never "100%" for a ratio below one and never "0%" for one above zero:
 * a check that failed four rows in ten thousand rounds to 100.0, and printing that beside the points
 * it lost would contradict the row it sits on. Whole numbers drop their decimals. */
export function percent(ratio: number, digits = 1): string {
  const factor = 10 ** digits;
  const rounded = Math.round(ratio * 100 * factor) / factor;
  if (ratio < 1 && rounded >= 100) return `>${(100 - 1 / factor).toFixed(digits)}%`;
  if (ratio > 0 && rounded <= 0) return `<${(1 / factor).toFixed(digits)}%`;
  return `${Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(digits)}%`;
}

// ---- ordering ---------------------------------------------------------------------------------

/* Scored first, then the two gaps a reader can act on, then the dimensions the tenant chose not to
 * weight. Total over `DimensionState`, so a fifth state is a compile error rather than a row that
 * sorts wherever `undefined` happens to land. */
const STATE_RANK = {
  scored: 0,
  no_evidence: 1,
  unmeasurable: 2,
  not_weighted: 3,
} as const satisfies Record<DimensionState, number>;

/** Scored dimensions by the points they hold, then the gaps, each in the platform's own order. */
export function orderedDimensions(
  dimensions: readonly DimensionFactor[],
): readonly DimensionFactor[] {
  return [...dimensions].sort(
    (a, b) =>
      STATE_RANK[a.state] - STATE_RANK[b.state] ||
      (b.points_available ?? 0) - (a.points_available ?? 0) ||
      DIMENSION_ORDER.indexOf(a.dimension) - DIMENSION_ORDER.indexOf(b.dimension),
  );
}

/** A weighted dimension this score does not include, and why. Its share went to the others. */
export interface LeftOut {
  readonly dimension: Dimension;
  readonly state: 'no_evidence' | 'unmeasurable';
  readonly modelShare: number | null;
}

export function leftOut(factors: ScoreFactors): readonly LeftOut[] {
  return orderedDimensions(factors.dimensions).flatMap((entry) =>
    entry.state === 'no_evidence' || entry.state === 'unmeasurable'
      ? [
          {
            dimension: entry.dimension,
            state: entry.state,
            modelShare: entry.model_share,
          },
        ]
      : [],
  );
}

/** The rules that lost points, most first — the API's own order, kept. */
export function hurting(rules: readonly RuleFactor[]): readonly RuleFactor[] {
  return rules.filter((rule) => rule.direction === 'negative');
}

/** The rules that kept every point they control, most points first. */
export function helping(rules: readonly RuleFactor[]): readonly RuleFactor[] {
  return rules.filter((rule) => rule.direction === 'positive');
}

// ---- the headline -----------------------------------------------------------------------------

/** The share of the points lost a headline names checks for. Most of the loss, in the fewest checks
 * — which is the answer to "what do I fix first". */
export const FIX_FIRST_SHARE = 0.8;

export interface FixFirst {
  readonly count: number;
  readonly points: number;
}

/** The fewest of the costliest checks whose losses together reach `FIX_FIRST_SHARE` of `lost`. */
export function fixFirst(costly: readonly RuleFactor[], lost: number): FixFirst {
  let count = 0;
  let total = 0;
  for (const rule of costly) {
    if (total >= lost * FIX_FIRST_SHARE - 1e-9) break;
    total += rule.points_lost;
    count += 1;
  }
  return { count, points: total };
}

export type Headline =
  | { readonly kind: 'nothingMeasured'; readonly silentRules: number | null }
  | { readonly kind: 'allPassed'; readonly checks: number }
  | { readonly kind: 'onlyOne'; readonly rule: string; readonly lost: number }
  | { readonly kind: 'alone'; readonly rule: string; readonly share: number }
  | {
      readonly kind: 'few';
      readonly count: number;
      readonly points: number;
      readonly lost: number;
      readonly rule: string;
    }
  | {
      readonly kind: 'many';
      readonly count: number;
      readonly lost: number;
      readonly rule: string;
      readonly ruleLost: number;
    };

/** What passing the costliest check would do, and what the checks the headline does not name cost. */
export interface Lift {
  readonly rule: string;
  readonly perTable: boolean;
  readonly from: number;
  readonly to: number;
  readonly rest: { readonly count: number; readonly points: number } | null;
}

export interface Verdict {
  readonly headline: Headline;
  readonly lift: Lift | null;
}

/** Above this many checks, the headline names the costliest and counts the rest. */
const FEW_CHECKS = 3;

export function verdict(factors: ScoreFactors): Verdict {
  const value = factors.value;
  if (value === null) {
    const silent = factors.context.find((note) => note.kind === 'rules_without_evidence');
    return {
      headline: { kind: 'nothingMeasured', silentRules: silent?.count ?? null },
      lift: null,
    };
  }
  const costly = hurting(factors.rules);
  const top = costly[0];
  if (top === undefined) {
    return { headline: { kind: 'allPassed', checks: factors.rules.length }, lift: null };
  }
  const lost = factors.points_lost ?? 100 - value;
  const first = fixFirst(costly, lost);
  const headline: Headline =
    costly.length === 1
      ? { kind: 'onlyOne', rule: top.rule_name, lost }
      : first.count === 1
        ? { kind: 'alone', rule: top.rule_name, share: top.points_lost / lost }
        : first.count <= FEW_CHECKS
          ? {
              kind: 'few',
              count: first.count,
              points: first.points,
              lost,
              rule: top.rule_name,
            }
          : {
              kind: 'many',
              count: costly.length,
              lost,
              rule: top.rule_name,
              ruleLost: top.points_lost,
            };
  const rest = costly.slice(Math.max(first.count, 1));
  return {
    headline,
    lift: {
      rule: top.rule_name,
      perTable: top.check?.scope === 'table',
      from: value,
      to: Math.min(100, value + top.points_lost),
      rest:
        rest.length === 0
          ? null
          : {
              count: rest.length,
              points: rest.reduce((sum, rule) => sum + rule.points_lost, 0),
            },
    },
  };
}

// ---- the 100 points, by dimension -------------------------------------------------------------

export interface Segment {
  readonly dimension: Dimension;
  readonly available: number;
  readonly earned: number;
  readonly lost: number;
  /** The dimension's own 0–100 result. */
  readonly value: number;
}

/** One segment per scored dimension, sized by the points it holds. Absent for a score that
 * measured nothing: there are no points to split. */
export function segments(factors: ScoreFactors): readonly Segment[] {
  return orderedDimensions(factors.dimensions).flatMap((entry) =>
    entry.state === 'scored' &&
    entry.value !== null &&
    entry.points_available !== null &&
    entry.points_earned !== null &&
    entry.points_lost !== null
      ? [
          {
            dimension: entry.dimension,
            available: entry.points_available,
            earned: entry.points_earned,
            lost: entry.points_lost,
            value: entry.value,
          },
        ]
      : [],
  );
}

// ---- how far it can be relied on --------------------------------------------------------------

/** A trust note: the API's six, plus one the client derives when every check produced evidence. */
export type TrustKind = ContextKind | 'evidence_complete';

export interface TrustNote {
  readonly kind: TrustKind;
  readonly direction: FactorDirection;
  readonly count: number | null;
  readonly total: number | null;
  readonly ratio: number | null;
  readonly dimensions: readonly Dimension[];
}

const DIRECTION_RANK = {
  negative: 0,
  neutral: 1,
  positive: 2,
} as const satisfies Record<FactorDirection, number>;

/** The notes that qualify the number, the ones that limit it first.
 *
 * `evidence_incomplete` folds into `rules_without_evidence` when both are present: they describe one
 * fact twice, and the second names how many. "Every check produced evidence" is derived from
 * `evidence_complete` **only when no rule measured nothing** — a run can finish every task while a
 * table with no rows leaves a rule silent, and saying both would contradict itself on one card.
 */
export function trustNotes(factors: ScoreFactors): readonly TrustNote[] {
  const silent = factors.context.some((note) => note.kind === 'rules_without_evidence');
  const notes: TrustNote[] = factors.context.filter(
    (note) => !(note.kind === 'evidence_incomplete' && silent),
  );
  if (factors.evidence_complete && factors.value !== null && !silent) {
    notes.push({
      kind: 'evidence_complete',
      direction: 'positive',
      count: factors.rules.length,
      total: null,
      ratio: null,
      dimensions: [],
    });
  }
  return notes.sort((a, b) => DIRECTION_RANK[a.direction] - DIRECTION_RANK[b.direction]);
}

/** How many notes limit the number. */
export function cautions(notes: readonly TrustNote[]): number {
  return notes.filter((note) => note.direction === 'negative').length;
}

// ---- the factor list --------------------------------------------------------------------------

export type FactorView = 'hurting' | 'helping' | 'all';

/** Rows shown before "Show more". The costliest come first, so the fold hides the least. */
export const FACTORS_SHOWN = 8;

/** A search box earns its place only once a list is longer than a reader scans at a glance. */
export const SEARCH_FROM = 12;

export function defaultView(rules: readonly RuleFactor[]): FactorView {
  return hurting(rules).length > 0 ? 'hurting' : 'helping';
}

export interface FactorFilter {
  readonly view: FactorView;
  readonly dimension: Dimension | null;
  readonly query: string;
}

/** The rows a filter keeps, in the API's order. A query matches a rule name or a checked column. */
export function filterFactors(
  rules: readonly RuleFactor[],
  { view, dimension, query }: FactorFilter,
): readonly RuleFactor[] {
  const needle = query.trim().toLowerCase();
  const pool =
    view === 'hurting' ? hurting(rules) : view === 'helping' ? helping(rules) : rules;
  return pool.filter(
    (rule) =>
      (dimension === null || rule.dimension === dimension) &&
      (needle === '' ||
        rule.rule_name.toLowerCase().includes(needle) ||
        (rule.check?.columns ?? []).some((column) =>
          column.toLowerCase().includes(needle),
        )),
  );
}

/** Each costly rule's place in the order to fix them, 1 first. Positive rules have none. */
export function fixRanks(rules: readonly RuleFactor[]): ReadonlyMap<string, number> {
  return new Map(hurting(rules).map((rule, index) => [rule.rule_version_id, index + 1]));
}

/** The most points any one check controls: the scale every row's meter is drawn against. */
export function largestSlice(rules: readonly RuleFactor[]): number {
  return Math.max(0, ...rules.map((rule) => rule.points_available));
}

export type PassLine =
  | { readonly kind: 'table'; readonly passed: boolean }
  | { readonly kind: 'allRows'; readonly rows: number }
  | {
      readonly kind: 'someRows';
      readonly pass: number;
      readonly failed: number;
      readonly rows: number;
    };

/** How the pass rate was counted. A table-level check has one result, not one per row. */
export function passLine(rule: RuleFactor): PassLine {
  if (rule.check?.scope === 'table')
    return { kind: 'table', passed: rule.pass_ratio >= 1 };
  return rule.violation_count === 0
    ? { kind: 'allRows', rows: rule.rows_evaluated }
    : {
        kind: 'someRows',
        pass: rule.pass_ratio,
        failed: rule.violation_count,
        rows: rule.rows_evaluated,
      };
}

// ---- what a check measures --------------------------------------------------------------------

export type CheckSentence =
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'notNull'; readonly columns: readonly string[] }
  | { readonly kind: 'unique'; readonly columns: readonly string[] }
  | {
      readonly kind: 'acceptedValues';
      readonly columns: readonly string[];
      readonly allowed: number | null;
    }
  | {
      readonly kind: 'range';
      readonly columns: readonly string[];
      readonly min: number | null;
      readonly max: number | null;
    }
  | {
      readonly kind: 'pattern';
      readonly columns: readonly string[];
      readonly pattern: string | null;
    }
  | {
      readonly kind: 'freshness';
      readonly columns: readonly string[];
      readonly limit: AgeLimit | null;
    }
  | {
      readonly kind: 'reference';
      readonly columns: readonly string[];
      readonly reference: string | null;
    }
  | { readonly kind: 'notEmpty' }
  | {
      readonly kind: 'other';
      readonly name: string;
      readonly columns: readonly string[];
    };

export interface AgeLimit {
  readonly unit: 'hours' | 'days';
  readonly count: number;
}

/** Whole days from two days up, so "72 hours" reads as "3 days" and "36 hours" stays as written. */
export function ageLimit(hours: number): AgeLimit {
  return hours >= 48 && hours % 24 === 0
    ? { unit: 'days', count: hours / 24 }
    : { unit: 'hours', count: hours };
}

/** The sentence a check reads as. A kind this screen does not know yet reads by its name rather than
 * failing, because the contract keeps `kind` an open string so a new rule kind is not an error. */
export function checkSentence(check: RuleCheck | null): CheckSentence {
  if (check === null) return { kind: 'unreadable' };
  const columns = check.columns;
  switch (check.kind) {
    case 'not_null':
      return { kind: 'notNull', columns };
    case 'unique':
      return { kind: 'unique', columns };
    case 'accepted_values':
      return { kind: 'acceptedValues', columns, allowed: check.allowed_values };
    case 'range':
      return { kind: 'range', columns, min: check.min, max: check.max };
    case 'pattern':
      return { kind: 'pattern', columns, pattern: check.pattern };
    case 'freshness':
      return {
        kind: 'freshness',
        columns,
        limit: check.max_age_hours === null ? null : ageLimit(check.max_age_hours),
      };
    case 'referential_integrity':
      return { kind: 'reference', columns, reference: check.reference };
    case 'row_count_anomaly':
      return { kind: 'notEmpty' };
    default:
      return { kind: 'other', name: check.kind.replace(/_/g, ' '), columns };
  }
}

// ---- how one rule's points are worked out -----------------------------------------------------

const SEVERITY_ORDER: readonly Severity[] = ['blocker', 'error', 'warning', 'info'];

export interface PeerGroup {
  readonly severity: Severity;
  readonly count: number;
  readonly weight: number;
}

export interface Arithmetic {
  readonly dimension: Dimension;
  readonly dimensionPoints: number;
  /** Present when the dimension counts for a different share of this score than configured, which
   * happens exactly when a weighted dimension is left out and its share is spread over the rest. */
  readonly reweighted: {
    readonly configured: number;
    readonly share: number;
    readonly reasons: readonly LeftOut[];
  } | null;
  /** The rules sharing the dimension, grouped by severity, in severity order. */
  readonly peers: readonly PeerGroup[];
  readonly peerWeight: number;
  readonly severity: Severity;
  readonly severityWeight: number;
  readonly shareOfDimension: number;
  readonly available: number;
  readonly pass: number;
  readonly earned: number;
  readonly lost: number;
  readonly perTable: boolean;
}

/** A difference in share smaller than this is rounding, not a redistribution. */
const SHARE_TOLERANCE = 0.0005;

export function arithmetic(factors: ScoreFactors, rule: RuleFactor): Arithmetic {
  const peers = factors.rules.filter((other) => other.dimension === rule.dimension);
  const dimension = factors.dimensions.find(
    (entry) => entry.dimension === rule.dimension,
  );
  const configured = dimension?.model_share ?? null;
  const share = dimension?.score_share ?? null;
  const reasons = leftOut(factors);
  return {
    dimension: rule.dimension,
    dimensionPoints:
      dimension?.points_available ??
      peers.reduce((sum, other) => sum + other.points_available, 0),
    reweighted:
      configured !== null &&
      share !== null &&
      reasons.length > 0 &&
      Math.abs(share - configured) > SHARE_TOLERANCE
        ? { configured, share, reasons }
        : null,
    peers: SEVERITY_ORDER.flatMap((severity) => {
      const group = peers.filter((other) => other.severity === severity);
      const first = group[0];
      return first === undefined
        ? []
        : [{ severity, count: group.length, weight: first.severity_weight }];
    }),
    peerWeight: peers.reduce((sum, other) => sum + other.severity_weight, 0),
    severity: rule.severity,
    severityWeight: rule.severity_weight,
    shareOfDimension: rule.share_of_dimension,
    available: rule.points_available,
    pass: rule.pass_ratio,
    earned: rule.points_earned,
    lost: rule.points_lost,
    perTable: rule.check?.scope === 'table',
  };
}

// ---- how the number is calculated -------------------------------------------------------------

export interface CalcRow {
  readonly dimension: Dimension;
  readonly state: DimensionState;
  readonly configured: number | null;
  readonly share: number | null;
  readonly available: number | null;
  readonly value: number | null;
  readonly earned: number | null;
  readonly lost: number | null;
  readonly rulesCounted: number;
  readonly rulesWithout: number;
}

export function calcRows(factors: ScoreFactors): readonly CalcRow[] {
  return orderedDimensions(factors.dimensions).map((entry) => ({
    dimension: entry.dimension,
    state: entry.state,
    configured: entry.model_share,
    share: entry.score_share,
    available: entry.points_available,
    value: entry.value,
    earned: entry.points_earned,
    lost: entry.points_lost,
    rulesCounted: entry.rules_counted,
    rulesWithout: entry.rules_without_evidence,
  }));
}

export type CalcNote =
  | { readonly kind: 'nothing' }
  | {
      readonly kind: 'single';
      readonly dimension: Dimension;
      readonly configured: number | null;
    }
  | {
      readonly kind: 'redistributed';
      readonly leftOut: readonly Dimension[];
      readonly measured: number;
      readonly example: {
        readonly dimension: Dimension;
        readonly weight: number;
        readonly total: number;
        readonly share: number;
      };
    }
  | { readonly kind: 'asConfigured' };

/** The one sentence the calculation needs: whose share moved, or that none did. A single scored
 * dimension is its own case because it carries the whole score — a "quality score" that is really
 * one dimension's score, which a reader must be told. */
export function calcNote(factors: ScoreFactors): CalcNote {
  if (factors.value === null) return { kind: 'nothing' };
  const scored = orderedDimensions(factors.dimensions).filter(
    (entry) => entry.state === 'scored',
  );
  const first = scored[0];
  if (first === undefined) return { kind: 'nothing' };
  if (scored.length === 1) {
    return { kind: 'single', dimension: first.dimension, configured: first.model_share };
  }
  const out = leftOut(factors);
  if (out.length === 0 || first.score_share === null) return { kind: 'asConfigured' };
  return {
    kind: 'redistributed',
    leftOut: out.map((entry) => entry.dimension),
    measured: scored.length,
    example: {
      dimension: first.dimension,
      weight: first.weight,
      total: scored.reduce((sum, entry) => sum + entry.weight, 0),
      share: first.score_share,
    },
  };
}

/** The severity weights the model used, in severity order, for the ones it names. */
export function severityWeights(
  factors: ScoreFactors,
): readonly { readonly severity: Severity; readonly weight: number }[] {
  return SEVERITY_ORDER.flatMap((severity) => {
    const weight = factors.severity_weights[severity];
    return weight === undefined ? [] : [{ severity, weight }];
  });
}
